<template>
	<div id="app" class="site" ref="app">
		<header class="site__header" ref="appHeader">
			<HeaderBar />
			<NavigationBar v-if="showNavigationBar" />
			<ToastContainer>
				<portal-target name="toastPortalDestination" />
				<Toast
					v-for="(toast, index) in toastMessages"
					:key="index"
					:slot="toast.slot || 'default'"
					:message="toast.message"
					:toastId="toast.toastId"
					:statusCode="toast.statusCode"
					:useIcon="toast.userIcon"
					:userDismissable="toast.userDismissable"
				/>
			</ToastContainer>
		</header>
		<main class="site__main">
			<router-view />
		</main>
		<footer class="site__footer" ref="appFooter">
			<FooterBar v-if="oidcIsAuthenticated" />
		</footer>
	</div>
</template>

<script>
// @ is an alias to /src
import HeaderBar from '@/site-components/header-bar/header-bar';
import FooterBar from '@/site-components/footer-bar/footer-bar';
import NavigationBar from '@/site-components/navigation-bar/navigation-bar';
import Toast from '@/components/notifications/toast/toast';
import ToastContainer from '@/components/notifications/toast-container/toast-container';

// vuex
import { mapGetters, mapMutations, mapActions } from 'vuex';
import AuthorizationMixin from '@/mixins/authorization/authorization-mixin.js';

export default {
	name: 'App',
	components: {
		HeaderBar,
		FooterBar,
		NavigationBar,
		Toast,
		ToastContainer
	},
	mixins: [AuthorizationMixin],
	computed: {
		...mapGetters('oidcStore', ['oidcIsAuthenticated', 'oidcAccessToken']),
		...mapGetters('toastMessages', ['toastMessages']),

		showNavigationBar() {
			return this.oidcIsAuthenticated && this.isInternalUser;
		}
	},
	created() {
		this.onResize();
		window.addEventListener('resize', this.onResize, { passive: true });
	},
	beforeDestroy() {
		if (typeof window !== 'undefined') {
			window.removeEventListener('resize', this.onResize, {
				passive: true
			});
		}
	},

	methods: {
		...mapMutations(['setScreenSize']),
		...mapActions('communicationHub', ['setHubConnectionAction']),
		...mapActions('logging', ['initLoggingAction']),
		...mapActions('menu', ['loadMenu']),
		onResize() {
			this.setScreenSize(window.innerWidth);
		},
		initLogging(userToken) {
			this.initLoggingAction({
				loggingEnabled: process.env.VUE_APP_CLIENT_LOGGING_ENABLED,
				userToken: userToken,
				sourceApplication:
					process.env.VUE_APP_LOGGING_SOURCE_APPLICATION,
				loggingApi: process.env.VUE_APP_CLIENT_LOGGING_API
			});
		}
	},
	watch: {
		oidcAccessToken(newValue) {
			if (newValue) {
				this.initLogging(newValue);
			}
		},
		async oidcIsAuthenticated(newValue) {
			if (newValue && this.isInternalUser) {
				try {
					await this.loadMenu();
				} catch (error) {
					console.log(error);
				}
			}
		},
		$route: {
			deep: true,
			immediate: true,
			handler(route) {
				const { query } = route;
				if (query) {
					if (query.rememberdays) {
						this.$store.dispatch('toastMessages/show', {
							toastId: 'rememberdays_success',
							message: `This device will be remembered for ${query.rememberdays} days`,
							statusCode: 200,
							slot: 'default'
						});
					}
					if (query.passwordreset === 'true') {
						this.$store.dispatch('toastMessages/show', {
							toastId: 'passwordreset_success',
							message:
								'The Password has been changed successfully!',
							statusCode: 200,
							slot: 'default'
						});
					}
				}
			}
		}
	}
};
</script>

<style lang="scss">
@import '@/scss';

/**
    * Application Page
    * Can use SCSS variables = TRUE
    *
    * This should only contain classes and styles used for the page layout.
*/

/*------------------------------------*\
  #LAYOUT STYLES
\*------------------------------------*/

.site {
	display: flex;
	flex-direction: column;
	//styles needed for footer to stick to bottom of screen but allow scrollable content
	min-height: 100vh;
	position: relative;
	&__header {
		background: $site-header-bkgd;
		padding: $padding-xs 0 $padding-xs;
	}

	&__main {
		padding-bottom: 45px; // equal to height of site__footer
		margin-bottom: $gutter-lg; // add some white space between page content and footer when needed
	}

	&__footer {
		background-color: $footer-bkgd;
		@include text-contrast($footer-bkgd, $white, $black);
		width: 100%;
		//styles needed for footer to stick to bottom of screen but allow scrollable content
		position: absolute;
		bottom: 0;
	}
}
</style>
